import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import AuthCallback from '../components/AuthCallback';
import ProtectedRoute from '../components/ProtectedRoute';
import BottomNav from '../components/BottomNav';

import LoginPage from '../pages/LoginPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import CoachRequestPage from '../pages/CoachRequestPage';
import DashboardPage from '../pages/DashboardPage';
import CreateWithAIPage from '../pages/CreateWithAIPage';
import WorkoutPreviewPage from '../pages/WorkoutPreviewPage';
import WorkoutDetailPage from '../pages/WorkoutDetailPage';
import SessionDetailPage from '../pages/SessionDetailPage';
import ProgressPage from '../pages/ProgressPage';
import CreateWorkoutPage from '../pages/CreateWorkoutPage';
import LogSessionPage from '../pages/LogSessionPage';
import CalendarPage from '../pages/CalendarPage';
import EditWorkoutPage from '../pages/EditWorkoutPage';
import ProfilePage from '../pages/ProfilePage';
import CoachSuccessPage from '../pages/CoachSuccessPage';
import CoachDashboard from '../pages/CoachDashboard';
import LinkCoachPage from '../pages/LinkCoachPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import SupportPage from '../pages/SupportPage';
import ShareImportPage from '../pages/ShareImportPage';
import CoachPaymentPage from '../pages/CoachPaymentPage';
import AthleteDetailPage from '../pages/AthleteDetailPage';
import CoachCreateWorkoutPage from '../pages/CoachCreateWorkoutPage';
import CoachWorkoutDetailPage from '../pages/CoachWorkoutDetailPage';
import ReportsPage from '../pages/ReportsPage';
import ReportDetailPage from '../pages/ReportDetailPage';
import SendReportPage from '../pages/SendReportPage';
import SendFeedbackPage from '../pages/SendFeedbackPage';
import DietsPage from '../pages/DietsPage';
import DietDetailPage from '../pages/DietDetailPage';
import BodyWeightPage from '../pages/BodyWeightPage';
import CustomizePage from '../pages/CustomizePage';
import WaterTrackingPage from '../pages/WaterTrackingPage';

const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'coach') return <Navigate to="/coach-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/coach-request" element={<CoachRequestPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/coach-payment" element={<CoachPaymentPage />} />
      <Route path="/coach-success" element={<CoachSuccessPage />} />
      <Route path="/coach-dashboard" element={<CoachDashboard />} />
      <Route path="/coach/athlete/:athleteId" element={<AthleteDetailPage />} />
      <Route path="/coach/athlete/:athleteId/create-workout" element={<CoachCreateWorkoutPage />} />
      <Route path="/coach/athlete/:athleteId/workout/:workoutId" element={<CoachWorkoutDetailPage />} />
      <Route path="/coach/athlete/:athleteId/workout/:workoutId/edit" element={<CoachCreateWorkoutPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/share/:shareId" element={<ShareImportPage />} />
      <Route path="/reports" element={
        <ProtectedRoute><ReportsPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/reports/new" element={
        <ProtectedRoute><SendReportPage /></ProtectedRoute>
      } />
      <Route path="/reports/:reportId/feedback" element={
        <ProtectedRoute><SendFeedbackPage /></ProtectedRoute>
      } />
      <Route path="/reports/:reportId" element={
        <ProtectedRoute><ReportDetailPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/diets" element={
        <ProtectedRoute><DietsPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/diets/:dietId" element={
        <ProtectedRoute><DietDetailPage /><BottomNav /></ProtectedRoute>
      } />

      <Route path="/link-coach" element={
        <ProtectedRoute><LinkCoachPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute coachRedirect="/coach-dashboard"><DashboardPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/body-weight" element={
        <ProtectedRoute><BodyWeightPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/water" element={
        <ProtectedRoute><WaterTrackingPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute><ProgressPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute><CalendarPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><ProfilePage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/customize" element={
        <ProtectedRoute><CustomizePage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/session/:sessionId" element={
        <ProtectedRoute><SessionDetailPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/workout/create" element={
        <ProtectedRoute><CreateWorkoutPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/create-with-ai" element={
        <ProtectedRoute><CreateWithAIPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/workout/preview" element={
        <ProtectedRoute><WorkoutPreviewPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/workout/:workoutId/log/:dayIndexLog" element={
        <ProtectedRoute><LogSessionPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/workout/:workoutId/edit" element={
        <ProtectedRoute><EditWorkoutPage /><BottomNav /></ProtectedRoute>
      } />
      <Route path="/workout/:workoutId" element={
        <ProtectedRoute><WorkoutDetailPage /><BottomNav /></ProtectedRoute>
      } />

      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
};

export default AppRouter;
